import type { FC, PointerEvent, ReactNode } from 'react'

import Image from 'next/image'
import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'

import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import { useFinePointer, usePrefersReducedMotion } from '@/hooks/use-media-query'
import { Chevron } from '@/components/generals/brand'
import { RollText } from '@/components/generals/roll-text'
import { brochure } from '@/constants/content'
import { siteConfig } from '@/constants/site'

// The brochure: the cover swings from a tilted, offered pose to face the reader as you scroll.
export const Brochure: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduced = usePrefersReducedMotion()
    const finePointer = useFinePointer()

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'center center']
    })

    const tiltX = useMotionValue(0)
    const tiltY = useMotionValue(0)
    const springTiltX = useSpring(tiltX, { stiffness: 120, damping: 18 })
    const springTiltY = useSpring(tiltY, { stiffness: 120, damping: 18 })

    const scrollRotateY = useTransform(scrollYProgress, [0, 1], [-32, 0])
    const scrollRotateX = useTransform(scrollYProgress, [0, 1], [14, 0])
    const rotateY = useTransform([scrollRotateY, springTiltY], ([scroll, tilt]) => (scroll as number) + (tilt as number))
    const rotateX = useTransform([scrollRotateX, springTiltX], ([scroll, tilt]) => (scroll as number) + (tilt as number))
    const scale = useTransform(scrollYProgress, [0, 1], [0.86, 1])
    const lift = useTransform(scrollYProgress, [0, 1], [120, 0])
    const echoY = useTransform(scrollYProgress, [0, 1], ['18%', '-6%'])
    const yearX = useTransform(scrollYProgress, [0, 1], ['12%', '0%'])

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (!finePointer || reduced) return

        const rect = event.currentTarget.getBoundingClientRect()
        const relativeX = (event.clientX - rect.left) / rect.width - 0.5
        const relativeY = (event.clientY - rect.top) / rect.height - 0.5

        tiltY.set(relativeX * 12)
        tiltX.set(relativeY * -10)
    }

    const resetTilt = () => {
        tiltX.set(0)
        tiltY.set(0)
    }

    return (
        <section
            ref={sectionRef}
            id='broshura'
            data-tone='ink'
            aria-labelledby='broshura-titulli'
            className='relative overflow-hidden px-5 py-28 md:px-8 md:py-40 lg:px-12'
        >
            <div className='grid grid-cols-12 items-center gap-x-5 gap-y-16'>
                <div className='relative z-10 col-span-12 lg:col-span-5'>
                    <p className='flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted'>
                        <span className='font-heading tabular-nums text-tone-accent'>06</span>
                        <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                        {brochure.label}
                    </p>

                    <h2 id='broshura-titulli' className='mt-8 font-heading font-bold tracking-[-0.045em]'>
                        <motion.span
                            style={reduced ? undefined : { x: yearX }}
                            className='block text-[21vw] font-black leading-[0.86] text-outline [--outline-color:var(--tone-accent)] [--outline-width:2px] [font-stretch:80%] lg:text-[9.5vw]'
                        >
                            {brochure.title}
                        </motion.span>

                        <span className='mt-3 block font-serif text-4xl font-normal italic tracking-normal sm:text-5xl lg:text-[3.6vw]'>
                            {brochure.titleAccent}
                        </span>
                    </h2>

                    <p className='mt-8 max-w-md text-lg leading-relaxed md:text-xl'>
                        {brochure.text}
                    </p>

                    <div className='mt-10 flex flex-wrap items-center gap-x-6 gap-y-3'>
                        <a
                            href={siteConfig.brochureUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='group inline-flex items-center gap-3 bg-tone-accent px-6 py-4 text-sm font-semibold text-tone-accent-fg'
                        >
                            <RollText text={brochure.cta} />
                            <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-45' />
                        </a>

                        <span className='text-xs font-semibold uppercase tracking-[0.18em]'>
                            {brochure.meta}
                        </span>
                    </div>
                </div>

                <div className='relative col-span-12 lg:col-span-7'>
                    <motion.div
                        aria-hidden='true'
                        style={reduced ? undefined : { y: echoY }}
                        className='pointer-events-none absolute -right-[8%] top-1/2 h-[120%] -translate-y-1/2 text-jap-purple/45'
                    >
                        <Chevron className='h-full w-auto' />
                    </motion.div>

                    <div
                        onPointerMove={onPointerMove}
                        onPointerLeave={resetTilt}
                        className='relative [perspective:1600px]'
                    >
                        <motion.a
                            href={siteConfig.brochureUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label={`${brochure.cta}, ${brochure.meta}`}
                            style={reduced ? undefined : { rotateX, rotateY, scale, y: lift }}
                            className='relative block aspect-[1570/1114] w-full shadow-[0_60px_120px_-40px_rgb(20_8_40/0.7)] [transform-style:preserve-3d]'
                        >
                            <Image
                                src='/images/broshura.png'
                                alt={brochure.alt}
                                fill
                                sizes='(min-width: 1024px) 56vw, 100vw'
                                className='object-cover'
                            />

                            <span
                                aria-hidden='true'
                                className='pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,rgb(255_255_255/0.18)_0%,transparent_38%,transparent_70%,rgb(0_0_0/0.18)_100%)]'
                            />
                        </motion.a>
                    </div>
                </div>
            </div>
        </section>
    )
}
